/**
 * Nueva cotización — documento informativo de precio para un cliente.
 *
 * El motor puede ser un chasis del inventario (se valida disponible con los
 * endpoints de la venta, pero NO se marca ni reserva) o un "modelo libre"
 * (marca/modelo/precio a mano, para cotizar mercancía que no está en stock).
 * El cliente no tiene que existir en el maestro: la cédula es opcional y si
 * existe autocompleta; basta nombre y teléfono.
 *
 * El plan de crédito es una simulación con la misma fórmula de la venta a
 * crédito (lib/calculocuotas); el backend recalcula al guardar y devuelve el
 * membrete de la empresa dueña del chasis (o la elegida) para imprimir.
 */
import React, { useState, useEffect, useCallback } from "react";
import Box from "@mui/material/Box";
import Grid from "@mui/material/Grid";
import TextField from "@mui/material/TextField";
import MenuItem from "@mui/material/MenuItem";
import Button from "@mui/material/Button";
import Typography from "@mui/material/Typography";
import Stack from "@mui/material/Stack";
import Switch from "@mui/material/Switch";
import FormControlLabel from "@mui/material/FormControlLabel";
import InputAdornment from "@mui/material/InputAdornment";
import ToggleButton from "@mui/material/ToggleButton";
import ToggleButtonGroup from "@mui/material/ToggleButtonGroup";
import Autocomplete, { createFilterOptions } from "@mui/material/Autocomplete";
import CircularProgress from "@mui/material/CircularProgress";
import SaveIcon from "@mui/icons-material/Save";
import RestartAltIcon from "@mui/icons-material/RestartAlt";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import PersonOutlineIcon from "@mui/icons-material/PersonOutline";
import DirectionsCarFilledOutlinedIcon from "@mui/icons-material/DirectionsCarFilledOutlined";
import RequestQuoteOutlinedIcon from "@mui/icons-material/RequestQuoteOutlined";
import { useGlobalMDAlert } from "context";
import { FP, PageHeader, fpInputSx } from "components/financepro";
import { useApi } from "lib/api";
import { useMultiempresa, SelectorEmpresa } from "lib/multiempresa";
import { calcularCuotas, useConfigCuotas, FORMAS_PAGO } from "lib/calculocuotas";
import CampoCuota from "components/cuotas/CampoCuota";
import { imprimirCotizacion } from "modules/cotizaciones/impresioncotizacion";

const FORM_INICIAL = {
  // Cliente
  cedula: "",
  nombres: "",
  apellidos: "",
  telefono: "",
  email: "",
  direccion: "",
  // Motor
  chasis: "",
  marca: "",
  modelo: "",
  colore: "",
  condicion: "",
  precio: "",
  // Cotización
  validez: "15",
  comentario: "",
  vendedor: "",
  // Plan de crédito
  inicial: "",
  interes: "",
  plazo: "",
  formadepago: "Meses",
};

// Filtro local del chasis: matchea por chasis, marca, modelo o color.
const filtrarChasis = createFilterOptions({
  stringify: (o) =>
    typeof o === "string" ? o : `${o.chasis} ${o.marca || ""} ${o.modelo || ""} ${o.colore || ""}`,
  limit: 60,
});

const fmtMoney = (v) => {
  const n = typeof v === "number" ? v : parseFloat(String(v || "").replace(/,/g, ""));
  if (Number.isNaN(n)) return "0.00";
  return n.toLocaleString("es-DO", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
};

const toNum = (v) => {
  const n = parseFloat(String(v ?? "").replace(/,/g, ""));
  return Number.isNaN(n) ? 0 : n;
};

const formatCedula = (v) => {
  const d = String(v || "")
    .replace(/\D/g, "")
    .slice(0, 11);
  const p1 = d.slice(0, 3);
  const p2 = d.slice(3, 10);
  const p3 = d.slice(10, 11);
  let out = p1;
  if (p2) out += `-${p2}`;
  if (p3) out += `-${p3}`;
  return out;
};

const btnBase = { textTransform: "none", borderRadius: 1.8, fontWeight: 700, boxShadow: "none" };
const btnSolid = (bg, hover) => ({
  ...btnBase,
  bgcolor: bg,
  color: "#FFFFFF",
  "&:hover": { bgcolor: hover, boxShadow: "none", color: "#FFFFFF" },
  "&.Mui-disabled": { bgcolor: `${bg}66`, color: "#FFFFFF" },
});
const btnOutline = (c) => ({
  ...btnBase,
  color: c,
  bgcolor: "#FFFFFF",
  border: `1px solid ${c}`,
  "&:hover": { bgcolor: `${c}12`, border: `1px solid ${c}`, color: c },
});

const sectionBox = {
  border: `1px solid ${FP.color.border}`,
  borderRadius: 2.5,
  p: { xs: 2, sm: 2.6 },
  mb: 2.4,
  bgcolor: FP.color.cardBg,
  boxShadow: FP.shadow.soft,
};

function SectionTitle({ icon: Icon, children, color }) {
  return (
    <Box sx={{ display: "flex", alignItems: "center", gap: 1.2, mb: 2 }}>
      <Box
        sx={{
          width: 32,
          height: 32,
          borderRadius: 1.4,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          bgcolor: `${color}14`,
          color,
        }}
      >
        <Icon sx={{ fontSize: 19 }} />
      </Box>
      <Typography
        sx={{
          fontWeight: 800,
          fontSize: "0.74rem",
          letterSpacing: "0.1em",
          textTransform: "uppercase",
          color: FP.color.textSecondary,
        }}
      >
        {children}
      </Typography>
    </Box>
  );
}

export default function CrearCotizacion({ embedded = false, onExit, onSaved } = {}) {
  const { showAlert } = useGlobalMDAlert();
  const { post } = useApi();
  const { redondeo } = useConfigCuotas();
  const { activo: multiActivo, empresas } = useMultiempresa();

  const [form, setForm] = useState(FORM_INICIAL);
  const setF = (k) => (e) => setForm((p) => ({ ...p, [k]: e.target.value }));

  // "inventario" (chasis validado) o "libre" (marca/modelo/precio a mano).
  const [modoMotor, setModoMotor] = useState("inventario");
  const [empresa, setEmpresa] = useState(1);
  const [conCredito, setConCredito] = useState(false);

  // ---- Cliente ----
  const buscarPorCedula = useCallback(async () => {
    const ced = form.cedula.trim();
    if (ced.length < 11) return;
    const { ok, data } = await post("/api/ventaproducto_buscarcliente", { cedula: ced });
    if (!ok) return;
    if (data.solicitud) {
      const s = data.solicitud;
      setForm((p) => ({
        ...p,
        nombres: s.nombres || p.nombres,
        apellidos: s.apellidos || p.apellidos,
        email: s.email || p.email,
        direccion: s.direccion || p.direccion,
        telefono: s.telefono || s.celular || p.telefono,
      }));
    }
  }, [form.cedula, post]);

  // ---- Chasis ----
  const [chasisOpts, setChasisOpts] = useState([]);
  const [loadingChasis, setLoadingChasis] = useState(false);
  const [chasisValidado, setChasisValidado] = useState(false);

  const cargarChasis = useCallback(async () => {
    setLoadingChasis(true);
    try {
      const { ok, data } = await post("/api/ventaproducto_chasis", {});
      if (ok && Array.isArray(data.chasis)) setChasisOpts(data.chasis);
    } finally {
      setLoadingChasis(false);
    }
  }, [post]);

  // Catálogo marca/modelo con precio de lista (Configuración → Catálogos):
  // autocompleta el modelo libre y prellena el precio de contado, que sigue
  // libre (la cotización no valida mínimo).
  const [marcamodelo, setMarcamodelo] = useState([]);

  useEffect(() => {
    cargarChasis();
    post("/api/cotizaciones_marcamodelo", {}).then(({ ok, data }) => {
      if (ok && Array.isArray(data.marcamodelo)) setMarcamodelo(data.marcamodelo);
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const marcasLibre = [...new Set(marcamodelo.map((m) => m.marca))];
  const modelosLibre = [
    ...new Set(
      marcamodelo
        .filter(
          (m) =>
            !form.marca ||
            (m.marca || "").toUpperCase() === form.marca.trim().toUpperCase()
        )
        .map((m) => m.modelo)
    ),
  ];

  const precioDeCatalogo = (marca, modelo) => {
    const cat = marcamodelo.find(
      (m) =>
        (m.marca || "").toUpperCase() === (marca || "").trim().toUpperCase() &&
        (m.modelo || "").toUpperCase() === (modelo || "").trim().toUpperCase()
    );
    return cat && Number(cat.precio) > 0 ? String(cat.precio) : null;
  };

  const limpiarMotor = () =>
    setForm((p) => ({
      ...p,
      chasis: "",
      marca: "",
      modelo: "",
      colore: "",
      condicion: "",
      precio: "",
    }));

  const validarChasis = useCallback(
    async (chasis) => {
      if (!chasis) return;
      const { ok, data } = await post("/api/ventaproducto_validarchasis", { chasis });
      if (!ok) {
        showAlert({ message: data.Error || "No se pudo validar el chasis", color: "error" });
        setChasisValidado(false);
        limpiarMotor();
        return;
      }
      // A diferencia de la venta al contado, un inicial registrado no impide
      // cotizar: la cotización no compromete el chasis.
      const precio = toNum(data.preciobase);
      setChasisValidado(true);
      setForm((p) => ({
        ...p,
        chasis: data.chasis,
        marca: data.marca,
        modelo: data.modelo,
        colore: data.colore,
        precio: precio ? String(precio) : "",
      }));
    },
    [post, showAlert]
  );

  const cambiarModoMotor = (_e, v) => {
    if (!v) return;
    setModoMotor(v);
    setChasisValidado(false);
    limpiarMotor();
  };

  // ---- Simulación de crédito (en vivo) ----
  const precio = toNum(form.precio);
  const inicial = toNum(form.inicial);
  const financia = Math.max(precio - inicial, 0);
  const simulacion = conCredito
    ? calcularCuotas(financia, toNum(form.plazo), form.formadepago, toNum(form.interes), redondeo)
    : null;

  // ---- Guardar ----
  const [guardando, setGuardando] = useState(false);

  const limpiar = () => {
    setForm(FORM_INICIAL);
    setChasisValidado(false);
    setConCredito(false);
  };

  const guardar = async () => {
    if (!form.nombres.trim() || !form.telefono.trim()) {
      showAlert({ message: "Nombre y teléfono del cliente son requeridos", color: "warning" });
      return;
    }
    if (modoMotor === "inventario" && (!chasisValidado || !form.chasis)) {
      showAlert({ message: "Seleccione un chasis válido de la lista", color: "warning" });
      return;
    }
    if (modoMotor === "libre" && !form.marca.trim()) {
      showAlert({ message: "Indique la marca del motor", color: "warning" });
      return;
    }
    if (precio <= 0) {
      showAlert({ message: "Indique el precio de contado", color: "warning" });
      return;
    }
    if (conCredito) {
      if (inicial >= precio) {
        showAlert({ message: "El inicial debe ser menor que el precio", color: "warning" });
        return;
      }
      if (!simulacion) {
        showAlert({ message: "Complete interés y plazo del plan de crédito", color: "warning" });
        return;
      }
    }
    setGuardando(true);
    try {
      const { ok, data } = await post("/api/cotizacion_crear", {
        ...form,
        chasis: modoMotor === "inventario" ? form.chasis : "",
        concredito: conCredito ? "S" : "N",
        empresa,
      });
      if (ok) {
        showAlert({
          message: `${data.mensajeok || "Cotización registrada"} — #${data.id}`,
          color: "success",
        });
        // El backend devuelve la fila canónica y el membrete de la empresa dueña.
        if (data.cotizacion) imprimirCotizacion(data);
        limpiar();
        if (onSaved) onSaved();
        return;
      }
      showAlert({ message: data.Error || "Error al guardar", color: "error" });
    } catch {
      showAlert({ message: "Error de red o servidor", color: "error" });
    } finally {
      setGuardando(false);
    }
  };

  return (
    <Box component="main" sx={{ minHeight: "100vh", bgcolor: FP.color.surface }}>
      <PageHeader
        title="Nueva Cotización"
        subtitle="Documento informativo de precio; no compromete la venta ni reserva el chasis"
        cta={
          <Stack direction="row" spacing={1.2}>
            {embedded && (
              <Button startIcon={<ArrowBackIcon />} onClick={onExit} sx={btnOutline(FP.color.navy)}>
                Volver
              </Button>
            )}
            <Button startIcon={<RestartAltIcon />} onClick={limpiar} sx={btnOutline(FP.color.navy)}>
              Nueva
            </Button>
          </Stack>
        }
      />

      <Box sx={{ p: { xs: 2, md: 3 } }}>
        {/* Cliente */}
        <Box sx={sectionBox}>
          <SectionTitle icon={PersonOutlineIcon} color={FP.color.accentBlue}>
            Cliente
          </SectionTitle>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={4}>
              <TextField
                fullWidth
                label="Cédula (opcional)"
                value={form.cedula}
                onChange={(e) => setForm((p) => ({ ...p, cedula: formatCedula(e.target.value) }))}
                onBlur={buscarPorCedula}
                placeholder="000-0000000-0"
                inputProps={{ maxLength: 13 }}
                sx={fpInputSx}
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField
                fullWidth
                required
                label="Nombres"
                value={form.nombres}
                onChange={setF("nombres")}
                sx={fpInputSx}
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField
                fullWidth
                label="Apellidos"
                value={form.apellidos}
                onChange={setF("apellidos")}
                sx={fpInputSx}
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField
                fullWidth
                required
                label="Teléfono"
                value={form.telefono}
                onChange={setF("telefono")}
                sx={fpInputSx}
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField
                fullWidth
                label="Email"
                value={form.email}
                onChange={setF("email")}
                sx={fpInputSx}
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField
                fullWidth
                label="Dirección"
                value={form.direccion}
                onChange={setF("direccion")}
                sx={fpInputSx}
              />
            </Grid>
          </Grid>
        </Box>

        {/* Motor */}
        <Box sx={sectionBox}>
          <SectionTitle icon={DirectionsCarFilledOutlinedIcon} color={FP.color.success}>
            Motor / Vehículo
          </SectionTitle>
          <ToggleButtonGroup
            exclusive
            size="small"
            value={modoMotor}
            onChange={cambiarModoMotor}
            sx={{ mb: 2 }}
          >
            <ToggleButton value="inventario" sx={{ textTransform: "none", fontWeight: 700, px: 2 }}>
              Chasis del inventario
            </ToggleButton>
            <ToggleButton value="libre" sx={{ textTransform: "none", fontWeight: 700, px: 2 }}>
              Modelo libre (sin chasis)
            </ToggleButton>
          </ToggleButtonGroup>
          <Grid container spacing={2}>
            {modoMotor === "inventario" ? (
              <>
                <Grid item xs={12} sm={6} md={3}>
                  <Autocomplete
                    freeSolo
                    autoHighlight
                    selectOnFocus
                    handleHomeEndKeys
                    filterOptions={filtrarChasis}
                    options={chasisOpts}
                    loading={loadingChasis}
                    value={form.chasis || null}
                    getOptionLabel={(o) => (typeof o === "string" ? o : o.chasis || "")}
                    isOptionEqualToValue={(o, v) => (o.chasis || o) === (v.chasis || v)}
                    renderOption={(props, o) => (
                      <li {...props} key={o.chasis}>
                        <Box>
                          <Typography sx={{ fontWeight: 700, fontSize: "0.85rem" }}>
                            {o.chasis}
                          </Typography>
                          <Typography sx={{ fontSize: "0.72rem", color: FP.color.textMuted }}>
                            {[o.marca, o.modelo, o.colore].filter(Boolean).join(" · ")}
                          </Typography>
                        </Box>
                      </li>
                    )}
                    onChange={(_, v) => {
                      const ch = typeof v === "string" ? v : v?.chasis || "";
                      if (ch) validarChasis(ch);
                      else {
                        setChasisValidado(false);
                        limpiarMotor();
                      }
                    }}
                    renderInput={(params) => (
                      <TextField {...params} label="Chasis" sx={fpInputSx} />
                    )}
                  />
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                  <TextField
                    fullWidth
                    label="Marca"
                    value={form.marca}
                    InputProps={{ readOnly: true }}
                    sx={fpInputSx}
                  />
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                  <TextField
                    fullWidth
                    label="Modelo"
                    value={form.modelo}
                    InputProps={{ readOnly: true }}
                    sx={fpInputSx}
                  />
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                  <TextField
                    fullWidth
                    label="Color"
                    value={form.colore}
                    InputProps={{ readOnly: true }}
                    sx={fpInputSx}
                  />
                </Grid>
              </>
            ) : (
              <>
                <Grid item xs={12} sm={6} md={3}>
                  <Autocomplete
                    freeSolo
                    options={marcasLibre}
                    value={form.marca}
                    onInputChange={(_, v) =>
                      setForm((p) => ({ ...p, marca: v || "", modelo: "" }))
                    }
                    renderInput={(params) => (
                      <TextField {...params} required label="Marca" sx={fpInputSx} />
                    )}
                  />
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                  <Autocomplete
                    freeSolo
                    options={modelosLibre}
                    value={form.modelo}
                    onInputChange={(_, v) =>
                      setForm((p) => {
                        const modelo = v || "";
                        const precioCat = precioDeCatalogo(p.marca, modelo);
                        return { ...p, modelo, ...(precioCat ? { precio: precioCat } : {}) };
                      })
                    }
                    renderInput={(params) => (
                      <TextField {...params} label="Modelo" sx={fpInputSx} />
                    )}
                  />
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                  <TextField
                    fullWidth
                    label="Color"
                    value={form.colore}
                    onChange={setF("colore")}
                    sx={fpInputSx}
                  />
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                  <TextField
                    select
                    fullWidth
                    label="Condición"
                    value={form.condicion}
                    onChange={setF("condicion")}
                    sx={fpInputSx}
                  >
                    <MenuItem value="">—</MenuItem>
                    <MenuItem value="Nuevo">Nuevo</MenuItem>
                    <MenuItem value="Usado">Usado</MenuItem>
                  </TextField>
                </Grid>
                {multiActivo && (
                  <Grid item xs={12} sm={6} md={3}>
                    <SelectorEmpresa
                      value={empresa}
                      onChange={setEmpresa}
                      empresas={empresas}
                      conTodas={false}
                    />
                  </Grid>
                )}
              </>
            )}
            <Grid item xs={12} sm={4} md={3}>
              <TextField
                fullWidth
                type="number"
                label="Precio de contado"
                value={form.precio}
                onChange={setF("precio")}
                sx={fpInputSx}
                InputProps={{
                  startAdornment: <InputAdornment position="start">RD$</InputAdornment>,
                }}
              />
            </Grid>
            <Grid item xs={12} sm={4} md={3}>
              <TextField
                fullWidth
                type="number"
                label="Validez (días)"
                value={form.validez}
                onChange={setF("validez")}
                sx={fpInputSx}
              />
            </Grid>
            <Grid item xs={12} sm={4} md={3}>
              <TextField
                fullWidth
                label="Vendedor"
                value={form.vendedor}
                onChange={setF("vendedor")}
                sx={fpInputSx}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                multiline
                minRows={2}
                label="Comentario"
                value={form.comentario}
                onChange={setF("comentario")}
                inputProps={{ maxLength: 1000 }}
              />
            </Grid>
          </Grid>
        </Box>

        {/* Plan de crédito (simulación) */}
        <Box sx={{ ...sectionBox, mb: 0 }}>
          <Stack
            direction="row"
            alignItems="center"
            justifyContent="space-between"
            sx={{ mb: conCredito ? 2 : 0 }}
          >
            <SectionTitle icon={RequestQuoteOutlinedIcon} color={FP.color.navy}>
              Plan de crédito
            </SectionTitle>
            <FormControlLabel
              control={
                <Switch checked={conCredito} onChange={(e) => setConCredito(e.target.checked)} />
              }
              label={
                <Typography
                  sx={{ fontWeight: 700, fontSize: "0.85rem", color: FP.color.textSecondary }}
                >
                  Incluir plan de crédito
                </Typography>
              }
            />
          </Stack>
          {conCredito && (
            <Grid container spacing={2}>
              <Grid item xs={12} sm={4} md={3}>
                <TextField
                  fullWidth
                  type="number"
                  label="Inicial"
                  value={form.inicial}
                  onChange={setF("inicial")}
                  sx={fpInputSx}
                  InputProps={{
                    startAdornment: <InputAdornment position="start">RD$</InputAdornment>,
                  }}
                />
              </Grid>
              <Grid item xs={12} sm={4} md={3}>
                <TextField
                  fullWidth
                  type="number"
                  label="Interés (% mensual)"
                  value={form.interes}
                  onChange={setF("interes")}
                  sx={fpInputSx}
                />
              </Grid>
              <Grid item xs={6} sm={4} md={3}>
                <TextField
                  fullWidth
                  type="number"
                  label="Plazo (cuotas)"
                  value={form.plazo}
                  onChange={setF("plazo")}
                  sx={fpInputSx}
                />
              </Grid>
              <Grid item xs={6} sm={4} md={3}>
                <TextField
                  select
                  fullWidth
                  label="Forma de pago"
                  value={form.formadepago}
                  onChange={setF("formadepago")}
                  sx={fpInputSx}
                >
                  {FORMAS_PAGO.map((f) => (
                    <MenuItem key={f} value={f}>
                      {f}
                    </MenuItem>
                  ))}
                </TextField>
              </Grid>
              <Grid item xs={12} sm={4} md={3}>
                <TextField
                  fullWidth
                  label="Financiamiento"
                  value={fmtMoney(financia)}
                  InputProps={{
                    readOnly: true,
                    startAdornment: <InputAdornment position="start">RD$</InputAdornment>,
                  }}
                  sx={fpInputSx}
                />
              </Grid>
              <Grid item xs={12} sm={4} md={3}>
                <CampoCuota
                  editable={redondeo}
                  label="Valor de la cuota"
                  cuota={simulacion ? simulacion.valorcuota : null}
                  valorMostrado={simulacion ? fmtMoney(simulacion.valorcuota) : "—"}
                  financia={financia}
                  plazo={toNum(form.plazo)}
                  formadepago={form.formadepago}
                  onInteres={(interes) => setForm((p) => ({ ...p, interes }))}
                  sx={fpInputSx}
                />
              </Grid>
              <Grid item xs={12} sm={4} md={3}>
                <TextField
                  fullWidth
                  label="Total a pagar (inicial + cuotas)"
                  value={simulacion ? fmtMoney(simulacion.deudatotal + inicial) : "—"}
                  InputProps={{
                    readOnly: true,
                    startAdornment: <InputAdornment position="start">RD$</InputAdornment>,
                  }}
                  sx={fpInputSx}
                />
              </Grid>
            </Grid>
          )}

          <Stack
            direction="row"
            justifyContent="space-between"
            alignItems="center"
            sx={{ mt: 2.5 }}
          >
            <Typography sx={{ fontSize: "1.05rem", fontWeight: 800, color: FP.color.textPrimary }}>
              Precio de contado: RD$ {fmtMoney(form.precio)}
            </Typography>
            <Button
              size="large"
              startIcon={guardando ? <CircularProgress size={18} color="inherit" /> : <SaveIcon />}
              onClick={guardar}
              disabled={guardando}
              sx={{ ...btnSolid(FP.color.navy, FP.color.navySoft), py: 1.2, px: 4 }}
            >
              Guardar e imprimir
            </Button>
          </Stack>
        </Box>
      </Box>
    </Box>
  );
}
